
import { BrandRole } from './types';
import { userLookupService } from './services/userLookupService';
import { accessService } from './services/accessService';
import { teamMemberService } from './services/teamMemberService';
import { supabase } from '@/integrations/supabase/client';

export const brandTeamService = {
  async checkUserAccess(brandId: string, userId: string) {
    return accessService.checkUserAccess(brandId, userId);
  },

  async getTeamMembers(brandId: string) {
    return teamMemberService.getTeamMembers(brandId);
  },

  async inviteMember(email: string, role: BrandRole, brandId: string, grantedBy: string, customRoleId?: string, createSquadMember?: boolean, squadType?: string): Promise<boolean> {
    console.log('=== BRAND TEAM SERVICE: STARTING MEMBER INVITATION ===');
    console.log('Invitation parameters:', { email, role, brandId, grantedBy, customRoleId });
    
    try {
      // Step 1: Find user by email
      console.log('=== STEP 1: USER LOOKUP ===');
      const { userId } = await userLookupService.findUserByEmail(email);
      console.log('User found with ID:', userId);

      // Step 2: Check if user already has access
      console.log('=== STEP 2: CHECKING EXISTING ACCESS ===');
      const existingAccess = await accessService.checkExistingAccess(userId, brandId);
      
      if (existingAccess) {
        console.log('User already has access:', existingAccess);
        throw new Error('User already has access to this brand');
      }
      console.log('No existing access found, proceeding...');

      // Step 3: Grant access
      console.log('=== STEP 3: GRANTING ACCESS ===');
      const success = await accessService.grantAccess(userId, brandId, role, grantedBy, customRoleId);
      
      // Step 4: Create squad member if requested
      if (createSquadMember && squadType) {
        await this.createSquadMember(userId, email, brandId, squadType);
      }
      
      return success;

    } catch (error: any) {
      console.error('=== BRAND TEAM SERVICE: MEMBER INVITATION FAILED ===');
      console.error('Error in inviteMember:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        stack: error.stack
      });
      
      // Re-throw with the original error message if it already contains user guidance
      if (error.message.includes('Please ask them to create an account first')) {
        throw error;
      } else if (error.message.includes('not found')) {
        throw new Error(`User with email "${email}" not found. Please ask them to create an account first and then try inviting them again.`);
      } else if (error.message.includes('already has access')) {
        throw new Error('This user already has access to this brand.');
      } else if (error.message.includes('constraint')) {
        throw new Error('Database error: User may not be properly registered in the system. Please ask them to create an account first.');
      } else {
        throw new Error(`Failed to invite team member: ${error.message}`);
      }
    }
  },

  async updateMemberRole(memberId: string, newRole: BrandRole, customRoleId?: string): Promise<boolean> {
    return teamMemberService.updateMemberRole(memberId, newRole, customRoleId);
  },

  async removeMember(memberId: string): Promise<boolean> {
    return teamMemberService.removeMember(memberId);
  },

  async createSquadMember(userId: string, email: string, brandId: string, squadType: string): Promise<void> {
    try {
      // Get user profile to extract names
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw profileError;
      }

      // Extract first and last names from full_name
      const fullName = profile?.full_name || email.split('@')[0];
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      // Create alias from first letters
      const alias = `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}` || email.charAt(0).toUpperCase();

      // Create squad member and get the new ID
      const { data: newMember, error: squadError } = await supabase
        .from('growth_squad')
        .insert({
          name: firstName,
          alias: alias,
          email: email,
          user_id: userId,
          brand_id: brandId,
          type: squadType,
          avatar: alias
        })
        .select('id')
        .single();

      if (squadError) {
        console.error('Error creating squad member:', squadError);
        throw squadError;
      }

      // Sync to contacts (matching pattern from useGrowthSquad.ts)
      const firstNameForContact = firstName || null;
      const lastNameForContact = lastName || null;

      const { data: contactId, error: contactError } = await supabase.rpc('upsert_os_contact', {
        p_brand_id: brandId,
        p_email: email,
        p_phone: null,
        p_first_name: firstNameForContact,
        p_last_name: lastNameForContact,
        p_organization: null,
        p_title: null,
        p_user_id: userId,
        p_module: ['GrowthOS'],
        p_activity: ['squad member'],
        p_source_table: 'growth_squad',
        p_source_id: newMember.id,
      });

      if (contactError) {
        console.warn('Could not sync squad member to contacts:', contactError);
      } else if (contactId) {
        await supabase
          .from('growth_squad')
          .update({ contact_id: contactId })
          .eq('id', newMember.id);
      }
    } catch (error) {
      console.error('Failed to create squad member:', error);
      throw error;
    }
  }
};
